import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Logo from 'assets/logo.svg'
import { useThunkDispatch } from 'models/thunk'
import { ILoginForm } from 'lib/forms/Login/login.form.models'
import { loginSchema } from 'lib/forms/Login/login.form.schemas'
import useDialog from 'hooks/Shared/useDialog'
import { getProfile, getRole } from 'actions/account'
import { login, setAuthorization } from 'actions/user'
import { getWorkspaceList } from 'actions/workspaces'
import withTextInputController from 'hocs/withTextInputController'
import Button from 'components/Shared/Button/Button'
import FormTextInput from 'components/FormComponents/FormTextInput/FormTextInput'
import './styles.scss'

interface ILoginFormProps {
  onClickRegister: () => void
  onClickForgetPassword: () => void
}

const ControlledTextInput = withTextInputController(FormTextInput)

const LoginFormContainer = ({
  onClickForgetPassword,
  onClickRegister,
}: ILoginFormProps) => {
  const dispatch = useDispatch()
  const thunkDispatch = useThunkDispatch()

  const intl = useIntl()
  const dialog = useDialog()

  const [loading, setLoading] = useState<boolean>(false)

  const methods = useForm<ILoginForm>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(loginSchema),
  })

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = methods

  const onSubmit = async (v: ILoginForm) => {
    setLoading(true)
    try {
      await thunkDispatch(login(v.email, v.password))
      await thunkDispatch(getRole())
      await thunkDispatch(getProfile())
      await thunkDispatch(getWorkspaceList())
      dispatch(setAuthorization(true))
    } catch (err) {
      setLoading(false)
      dialog.danger({
        subtitle: 'login.error.message',
      })
    }
  }

  return (
    <div className="container-login-form">
      <a
        className="section logo"
        href="https://www.simmple.mx/"
        target="_blank"
        rel="noopener noreferrer">
        <img src={Logo} alt="Logo" />
      </a>
      <div className="section form">
        <h3>{intl.formatMessage({ id: 'login.title' })}</h3>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ControlledTextInput
            control={control}
            name="email"
            label={intl.formatMessage({ id: 'form.email' })}
            inputProps={{ maxLength: 60 }}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
          <ControlledTextInput
            control={control}
            name="password"
            label={intl.formatMessage({ id: 'form.password' })}
            type="password"
            inputProps={{
              maxLength: 60,
            }}
            error={!!errors.password}
            helperText={errors.password?.message}
          />
          <Button
            id="loginBtn"
            color="secondary"
            type="submit"
            variant="contained"
            fullWidth
            disabled={!isValid}
            activity={loading}>
            {intl.formatMessage({ id: 'button.login' })}
          </Button>
          <Button
            id="recoverBtn"
            color="primary"
            onClick={onClickForgetPassword}
            fullWidth
            disabled={loading}>
            {intl.formatMessage({ id: 'login.button.recover' })}
          </Button>
        </form>
      </div>
      <div className="section no-account">
        <p>{intl.formatMessage({ id: 'login.no_account.title' })}</p>
        <Button
          id="registerBtn"
          color="secondary"
          variant="link"
          onClick={onClickRegister}>
          {intl.formatMessage({ id: 'login.no_account.link' })}
        </Button>
      </div>
    </div>
  )
}

export default LoginFormContainer
